import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  buildingServicesApi,
  CreateConstructionRegistrationRequest,
  UpdateConstructionRegistrationRequest,
  ConstructionRegistration,
  BuildingServiceSettings,
} from '../../api/buildingServicesApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { apartmentsApi, Apartment } from '../../api/apartmentsApi';
import { useAuth } from '../../contexts/AuthContext';
import { resolveDefaultBuildingId, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps } from '../../utils/buildingSelection';
import { RegistrationActivityLogsPanel, RegistrationActivityLog } from '../../components/building-services/RegistrationActivityLogsPanel';
import { formatVndInput, parseVndInput } from '../../utils/vndFormat';
import {
  advanceBookingWindowError,
  buildAdvanceBookingDatetimeLocalBounds,
  resolveServiceAdvanceBookingDays,
} from '../../utils/advance-booking-days.util';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { toVietnamDatetimeLocalValue } from '../../utils/vietnamDateTime';

interface FormData {
  apartmentId: string;
  contractorName: string;
  contractorPhone: string;
  startDateTime: string;
  endDateTime: string;
  constructionContent: string;
  notes: string;
  attachments: string[];
  agreeToRegulations: boolean;
  depositAmount: string;
}

const AddEditConstructionRegistration: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isEditing = !!id;

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const [serviceSettings, setServiceSettings] = useState<BuildingServiceSettings | null>(null);
  const [registration, setRegistration] = useState<ConstructionRegistration | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    apartmentId: '',
    contractorName: '',
    contractorPhone: '',
    startDateTime: '',
    endDateTime: '',
    constructionContent: '',
    notes: '',
    attachments: [],
    agreeToRegulations: false,
    depositAmount: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activityLogs, setActivityLogs] = useState<RegistrationActivityLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const advanceBookingBounds = useMemo(
    () => resolveServiceAdvanceBookingDays(serviceSettings, user?.role),
    [serviceSettings, user?.role],
  );
  const datetimeLocalBounds = useMemo(
    () => buildAdvanceBookingDatetimeLocalBounds(advanceBookingBounds),
    [advanceBookingBounds],
  );
  const allowNegativeMin = user?.role !== 'RESIDENT';
  const endDatetimeMin =
    formData.startDateTime && formData.startDateTime > datetimeLocalBounds.min
      ? formData.startDateTime
      : datetimeLocalBounds.min;

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (isEditing && buildings.length > 0) {
      loadRegistration();
    }
  }, [isEditing, id, buildings.length]);

  useEffect(() => {
    if (selectedBuilding) {
      loadApartments();
      loadServiceSettings();
    }
  }, [selectedBuilding]);

  // Auto-set agreeToRegulations to true for staff, superadmin, and operator users
  useEffect(() => {
    if (user?.role === 'STAFF' || user?.role === 'SUPERADMIN' || user?.role === 'OPERATOR') {
      setFormData(prev => ({ ...prev, agreeToRegulations: true }));
    }
  }, [user?.role]);

  const loadBuildings = async () => {
    try {
      const response = await buildingsApi.getBuildings();
      setBuildings(response);

      const buildingIdFromUrl = searchParams.get('buildingId');
      const defaultId = buildingIdFromUrl || resolveDefaultBuildingId(response, user);
      if (defaultId) {
        const buildingToSelect = response.find((b) => b.id === defaultId);
        if (buildingToSelect) setSelectedBuilding(buildingToSelect);
      }
    } catch (error) {
      console.error('Error loading buildings:', error);
      toast.error('Lỗi khi tải danh sách tòa nhà');
    }
  };

  const loadApartments = async () => {
    if (!selectedBuilding) return;
    
    try {
      console.log('Loading apartments for building:', selectedBuilding.id, selectedBuilding.name);
      const response = await apartmentsApi.getApartments();
      console.log('All apartments:', response);
      
      // Filter apartments by building ID
      let apartmentList = response.filter(apt => apt.buildingId === selectedBuilding.id);
      console.log('Filtered apartments for building:', apartmentList);
      
      // For residents, filter to only their apartments
      if (user?.role === 'RESIDENT') {
        // This would need to be implemented based on your user-apartment relationship
        // For now, we'll show all apartments but the backend will validate
      }
      
      setApartments(apartmentList);
    } catch (error) {
      console.error('Error loading apartments:', error);
      toast.error('Lỗi khi tải danh sách căn hộ');
    }
  };

  const loadServiceSettings = async () => {
    if (!selectedBuilding) return;
    
    try {
      const settings = await buildingServicesApi.getServiceSettings(selectedBuilding.id, 'CONSTRUCTION');
      setServiceSettings(settings);
    } catch (error) {
      console.error('Error loading service settings:', error);
    }
  };

  const loadRegistration = async () => {
    if (!id || buildings.length === 0) return;
    
    setLoading(true);
    try {
      const data = await buildingServicesApi.getConstructionRegistration(id);
      setRegistration(data);
      setLogsLoading(true);
      try {
        const logs = await buildingServicesApi.getConstructionActivityLogs(id);
        setActivityLogs(Array.isArray(logs) ? logs : []);
      } catch (logError) {
        console.error('Error loading activity logs:', logError);
        setActivityLogs([]);
      } finally {
        setLogsLoading(false);
      }
      
      // Find and set the building
      const building = buildings.find(b => b.id === data.buildingId);
      if (building) {
        setSelectedBuilding(building);
        
        // Load apartments for this building
        try {
          const apartmentResponse = await apartmentsApi.getApartments();
          const buildingApartments = apartmentResponse.filter(apt => apt.buildingId === building.id);
          setApartments(buildingApartments);
        } catch (error) {
          console.error('Error loading apartments:', error);
        }

        // Load service settings for this building
        try {
          const settings = await buildingServicesApi.getServiceSettings(building.id, 'CONSTRUCTION');
          setServiceSettings(settings);
        } catch (error) {
          console.error('Error loading service settings:', error);
        }
      }
      
      // Populate form data
      const startDateTime = toVietnamDatetimeLocalValue(data.startDate, data.startTime);
      const endDateTime = toVietnamDatetimeLocalValue(data.endDate, data.endTime);

      setFormData({
        apartmentId: data.apartmentId,
        contractorName: data.contractorName,
        contractorPhone: data.contractorPhone,
        startDateTime: startDateTime,
        endDateTime: endDateTime,
        constructionContent: data.constructionContent,
        notes: data.notes || '',
        attachments: data.attachments || [],
        agreeToRegulations: data.agreeToRegulations,
        depositAmount:
          data.depositAmount != null && data.depositAmount > 0
            ? formatVndInput(String(Math.round(data.depositAmount)))
            : '',
      });
    } catch (error) {
      console.error('Error loading registration:', error);
      toast.error('Lỗi khi tải thông tin đăng ký');
      navigate('/building-services/construction');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    console.log('Validating form data:', formData);

    if (!formData.apartmentId) {
      newErrors.apartmentId = 'Vui lòng chọn căn hộ';
    }

    if (!formData.contractorName.trim()) {
      newErrors.contractorName = 'Vui lòng nhập tên đơn vị thi công';
    }

    if (!formData.contractorPhone.trim()) {
      newErrors.contractorPhone = 'Vui lòng nhập số điện thoại';
    } else if (!/^[0-9+\-\s()]+$/.test(formData.contractorPhone)) {
      newErrors.contractorPhone = 'Số điện thoại không hợp lệ';
    }

    if (!formData.startDateTime) {
      newErrors.startDateTime = 'Vui lòng chọn thời gian bắt đầu';
    } else {
      const startDate = formData.startDateTime.split('T')[0];
      const startWindowError = advanceBookingWindowError(startDate, advanceBookingBounds, {
        allowNegativeMin,
      });
      if (startWindowError) {
        newErrors.startDateTime = startWindowError;
      }
    }

    if (!formData.endDateTime) {
      newErrors.endDateTime = 'Vui lòng chọn thời gian kết thúc';
    } else {
      const endDate = formData.endDateTime.split('T')[0];
      const endWindowError = advanceBookingWindowError(endDate, advanceBookingBounds, {
        allowNegativeMin,
      });
      if (endWindowError) {
        newErrors.endDateTime = endWindowError;
      }
    }

    if (formData.startDateTime && formData.endDateTime && formData.startDateTime >= formData.endDateTime) {
      newErrors.endDateTime = 'Thời gian kết thúc phải sau thời gian bắt đầu';
    }

    if (!formData.constructionContent.trim()) {
      newErrors.constructionContent = 'Vui lòng nhập nội dung thi công';
    }

    // Staff, Superadmin, and Operator users don't need to agree to regulations
    console.log('Validating agreeToRegulations:', {
      agreeToRegulations: formData.agreeToRegulations,
      userRole: user?.role,
      shouldValidate: !formData.agreeToRegulations && user?.role !== 'STAFF' && user?.role !== 'SUPERADMIN' && user?.role !== 'OPERATOR'
    });
    
    if (!formData.agreeToRegulations && user?.role !== 'STAFF' && user?.role !== 'SUPERADMIN' && user?.role !== 'OPERATOR') {
      newErrors.agreeToRegulations = 'Vui lòng đồng ý với quy định thi công';
    }

    if (formData.depositAmount && isNaN(parseFloat(formData.depositAmount))) {
      newErrors.depositAmount = 'Tiền cọc phải là số hợp lệ';
    }

    console.log('Validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Vui lòng kiểm tra lại thông tin');
      return;
    }

    setSubmitting(true);
    try {
      // Split datetime into date and time components
      const startDate = formData.startDateTime.split('T')[0];
      const startTime = formData.startDateTime.includes('T') ? formData.startDateTime.split('T')[1] : undefined;
      const endDate = formData.endDateTime.split('T')[0];
      const endTime = formData.endDateTime.includes('T') ? formData.endDateTime.split('T')[1] : undefined;

      const depositAmount = formData.depositAmount
        ? parseVndInput(formData.depositAmount)
        : undefined;
      const agreeToRegulations =
        user?.role === 'STAFF' || user?.role === 'SUPERADMIN' || user?.role === 'OPERATOR'
          ? true
          : formData.agreeToRegulations;

      if (isEditing && id) {
        const updateData: UpdateConstructionRegistrationRequest = {
          apartmentId: formData.apartmentId,
          contractorName: formData.contractorName.trim(),
          contractorPhone: formData.contractorPhone.trim(),
          startDate,
          startTime,
          endDate,
          endTime,
          constructionContent: formData.constructionContent.trim(),
          notes: formData.notes.trim() || undefined,
          attachments: formData.attachments,
          agreeToRegulations,
          depositAmount,
        };
        await buildingServicesApi.updateConstructionRegistration(id, updateData);
        toast.success('Cập nhật đăng ký thành công');
      } else {
        const createData: CreateConstructionRegistrationRequest = {
          apartmentId: formData.apartmentId,
          contractorName: formData.contractorName.trim(),
          contractorPhone: formData.contractorPhone.trim(),
          startDate,
          startTime,
          endDate,
          endTime,
          constructionContent: formData.constructionContent.trim(),
          notes: formData.notes.trim() || undefined,
          attachments: formData.attachments,
          agreeToRegulations,
          depositAmount,
        };
        await buildingServicesApi.createConstructionRegistration(createData);
        toast.success('Tạo đăng ký thành công');
      }
      
      navigate('/building-services/construction');
    } catch (error: any) {
      console.error('Error saving registration:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(getApiErrorMessage(error, 'Lỗi khi lưu đăng ký'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBuildingChange = (buildingId: string) => {
    const building = buildings.find(b => b.id === buildingId);
    setSelectedBuilding(building || null);
    setFormData(prev => ({ ...prev, apartmentId: '' }));
  };

  // Check if the registration is read-only (completed or cancelled)
  const isReadOnly = isEditing && (registration?.status === 'COMPLETED' || registration?.status === 'CANCELLED');

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Đang tải...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? (isReadOnly ? 'Xem đăng ký thi công' : 'Cập nhật đăng ký thi công') : 'Thêm đăng ký thi công'}
          </h1>
          <p className="text-gray-600">
            {isEditing ? (isReadOnly ? 'Xem thông tin đăng ký thi công' : 'Cập nhật thông tin đăng ký thi công') : 'Tạo đăng ký thi công mới'}
          </p>
        </div>
        <button
          onClick={() => navigate('/building-services/construction')}
          className="text-gray-600 hover:text-gray-800"
        >
          ← Quay lại
        </button>
      </div>

      {/* Read-only Notice for Completed/Cancelled Registrations */}
      {isReadOnly && (
        <div className={`${registration?.status === 'COMPLETED' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {registration?.status === 'COMPLETED' ? (
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${registration?.status === 'COMPLETED' ? 'text-green-800' : 'text-red-800'}`}>
                {registration?.status === 'COMPLETED' ? 'Đăng ký thi công đã hoàn thành' : 'Đăng ký thi công đã bị hủy'}
              </h3>
              <div className={`mt-2 text-sm ${registration?.status === 'COMPLETED' ? 'text-green-700' : 'text-red-700'}`}>
                <p>
                  {registration?.status === 'COMPLETED' 
                    ? 'Đăng ký này đã hoàn thành và không thể chỉnh sửa. Bạn chỉ có thể xem thông tin.'
                    : 'Đăng ký này đã bị hủy và không thể chỉnh sửa. Bạn chỉ có thể xem thông tin.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Status */}
      {selectedBuilding && serviceSettings && !serviceSettings.isActive && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Dịch vụ thi công hiện đang tạm ngưng
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Dịch vụ đăng ký thi công hiện tại đang không hoạt động. Vui lòng liên hệ ban quản lý để biết thêm chi tiết.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Basic Information */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Thông tin cơ bản
              </h3>

              {/* Building Selection */}
              {shouldShowBuildingSelector(buildings) && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tòa nhà <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedBuilding?.id || ''}
                    onChange={(e) => handleBuildingChange(e.target.value)}
                    disabled={isEditing || !needsBuildingSelection(buildings)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  >
                    <option value="">-- Chọn tòa nhà --</option>
                    {buildings.map(building => (
                      <option key={building.id} value={building.id}>
                        {building.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Apartment Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Căn hộ <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.apartmentId}
                  onChange={(e) => handleInputChange('apartmentId', e.target.value)}
                  disabled={isReadOnly || !selectedBuilding || (isEditing && !['SUPERADMIN', 'STAFF', 'OPERATOR'].includes(user?.role || ''))}
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                    errors.apartmentId ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">-- Chọn căn hộ --</option>
                  {apartments.map(apartment => (
                    <option key={apartment.id} value={apartment.id}>
                      {apartment.code} - {apartment.ownerName}
                    </option>
                  ))}
                </select>
                {errors.apartmentId && (
                  <p className="mt-1 text-sm text-red-600">{errors.apartmentId}</p>
                )}
              </div>

              {/* Contractor Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên đơn vị thi công <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.contractorName}
                  onChange={(e) => handleInputChange('contractorName', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                    errors.contractorName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Nhập tên đơn vị thi công"
                />
                {errors.contractorName && (
                  <p className="mt-1 text-sm text-red-600">{errors.contractorName}</p>
                )}
              </div>

              {/* Contractor Phone */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số điện thoại <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.contractorPhone}
                  onChange={(e) => handleInputChange('contractorPhone', e.target.value)}
                  disabled={isReadOnly}
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                    errors.contractorPhone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Nhập số điện thoại"
                />
                {errors.contractorPhone && (
                  <p className="mt-1 text-sm text-red-600">{errors.contractorPhone}</p>
                )}
              </div>

              {/* Construction Date and Time */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thời gian bắt đầu <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.startDateTime}
                    onChange={(e) => handleInputChange('startDateTime', e.target.value)}
                    disabled={isReadOnly}
                    min={datetimeLocalBounds.min}
                    max={datetimeLocalBounds.max}
                    className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                      errors.startDateTime ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.startDateTime && (
                    <p className="mt-1 text-sm text-red-600">{errors.startDateTime}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thời gian kết thúc <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.endDateTime}
                    onChange={(e) => handleInputChange('endDateTime', e.target.value)}
                    disabled={isReadOnly}
                    min={endDatetimeMin}
                    max={datetimeLocalBounds.max}
                    className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                      errors.endDateTime ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.endDateTime && (
                    <p className="mt-1 text-sm text-red-600">{errors.endDateTime}</p>
                  )}
                </div>
              </div>

              {/* Deposit Amount */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tiền cọc (VNĐ)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatVndInput(formData.depositAmount)}
                  onChange={(e) => handleInputChange('depositAmount', e.target.value.replace(/\./g, ''))}
                  disabled={isReadOnly}
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                    errors.depositAmount ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Nhập số tiền cọc (nếu có)"
                  min="0"
                />
                {errors.depositAmount && (
                  <p className="mt-1 text-sm text-red-600">{errors.depositAmount}</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Content and Regulations */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Nội dung thi công
              </h3>

              {/* Construction Content */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nội dung thi công <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.constructionContent}
                  onChange={(e) => handleInputChange('constructionContent', e.target.value)}
                  disabled={isReadOnly}
                  rows={6}
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                    errors.constructionContent ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Mô tả chi tiết nội dung thi công..."
                />
                {errors.constructionContent && (
                  <p className="mt-1 text-sm text-red-600">{errors.constructionContent}</p>
                )}
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ghi chú
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  disabled={isReadOnly}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  placeholder="Ghi chú thêm (nếu có)..."
                />
              </div>

              {/* File Attachments */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Đính kèm
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="mt-2">
                    <button
                      type="button"
                      disabled={isReadOnly}
                      className="text-blue-600 hover:text-blue-800 text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                      onClick={() => toast('Tính năng upload file đang được phát triển', { icon: 'ℹ️' })}
                    >
                      Chọn file để đính kèm
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG, PDF tối đa 10MB
                  </p>
                </div>
              </div>
            </div>

            {/* Regulations */}
            {serviceSettings?.regulations && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Quy định thi công
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700">
                    {serviceSettings.regulations}
                  </pre>
                </div>

                {/* Agreement Checkbox - Only show for non-staff, non-superadmin, non-operator users */}
                {user?.role !== 'STAFF' && user?.role !== 'SUPERADMIN' && user?.role !== 'OPERATOR' && (
                  <div className="mt-4">
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        checked={formData.agreeToRegulations}
                        onChange={(e) => handleInputChange('agreeToRegulations', e.target.checked)}
                        disabled={isReadOnly}
                        className={`mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 disabled:opacity-50 ${
                          errors.agreeToRegulations ? 'border-red-500' : ''
                        }`}
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Tôi đã đọc và đồng ý với quy định thi công của tòa nhà <span className="text-red-500">*</span>
                      </span>
                    </label>
                    {errors.agreeToRegulations && (
                      <p className="mt-1 text-sm text-red-600">{errors.agreeToRegulations}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {isEditing && (
          <RegistrationActivityLogsPanel logs={activityLogs} loading={logsLoading} />
        )}

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3 pt-6">
          <button
            type="button"
            onClick={() => navigate('/building-services/construction')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            {isReadOnly ? 'Quay lại' : 'Hủy'}
          </button>
          {!isReadOnly && (
            <button
              type="submit"
              disabled={submitting || (serviceSettings ? !serviceSettings.isActive : false)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Đang lưu...' : isEditing ? 'Cập nhật' : 'Tạo đăng ký'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddEditConstructionRegistration; 